<template>
  <b-form>
    <b-form-group
      label="Name"
      description="Field name, in camelCase, must be unique for this model"
      :state="errState($v.name)"
      invalid-feedback="A unique field name in camelCase format is required"
    >
      <b-form-input
        ref="name"
        v-model="$v.name.$model"
        type="text"
        :state="errState($v.name)"
      />
    </b-form-group>
    <b-form-group
      label="Type"
      description="Field type"
    >
      <b-form-select
        v-model="type"
        :options="fieldTypeChoices"
      />
    </b-form-group>
    <b-form-group
      v-if="isTextField && dbSupportsStringLength"
      label="Length"
      description="Length of the field (leave blank for unlimited field length)"
      invalid-feedback="If provided, a positive integer is required"
    >
      <b-form-input
        v-model="$v.length.$model"
        type="text"
        :state="errState($v.length)"
      />
    </b-form-group>
    <b-form-group
      v-if="isRelationalField"
      label="To"
      description="Related model that this field points to"
    >
      <b-form-select
        v-model="target"
        :options="fieldTargetChoices"
      />
    </b-form-group>

    <b-form-group
      v-if="type !== 'm2m'"
      description="Whether the field is required in client create/update requests"
    >
      <b-form-checkbox v-model="required">
        Required
      </b-form-checkbox>
    </b-form-group>
    <b-form-group
      v-if="type !== 'json' && !isRelationalField"
      description="Whether each model instance must have a unique value for this field"
    >
      <b-form-checkbox v-model="unique">
        Unique
      </b-form-checkbox>
    </b-form-group>
    <b-form-group
      v-if="type !== 'json' && !isRelationalField"
      description="Create database index for this field"
    >
      <b-form-checkbox v-model="index">
        Index
      </b-form-checkbox>
    </b-form-group>
    <b-form-group
      v-if="!isRelationalField"
      label="Default"
      description="Default value for the field (only if not required and unique)"
      invalid-feedback="Value doesn't match field type"
    >
      <small v-if="type == 'date'">
        Hint: you can use "<em>now</em>" as the default value for Date fields if you want
        the default to be time and date of the object creation.
      </small>
      <b-form-input
        v-model="$v.defaultValue.$model"
        type="text"
        :state="errState($v.defaultValue)"
        :disabled="required || unique"
      />
    </b-form-group>
    <b-form-group class="text-right">
      <b-button
        variant="danger"
        @click="removeField"
      >
        <b-icon icon="trash" />
        Remove field
      </b-button>
    </b-form-group>
  </b-form>
</template>

<style scoped lang="scss">
</style>

<script>
import { mapGetters } from 'vuex';
import { validationMixin } from 'vuelidate';
import {
  required,
  decimal,
  integer,
  url,
  email,
  minValue,
} from 'vuelidate/lib/validators';

const BOOL_VALUES = ['true', 'on', 'yes', '1', 't', 'y', 'false', 'off', 'no', '0', 'f', 'n'];

const mustBeCamelCase = (value) => !!value.match(/^[a-z][A-Za-z0-9]+$/);

function mustBeCorrectType(value) {
  const { type } = this;

  if (!value) return true;
  if (!type) return false;

  switch (type) {
    case 'integer':
      return integer(value);
    case 'number':
      return decimal(value);
    case 'url':
      return url(value);
    case 'email':
      return email(value);
    case 'date':
      return value === 'now' || !Number.isNaN(Date.parse(value));
    case 'boolean':
      return BOOL_VALUES.includes(value.toLowerCase());
    case 'json':
      try {
        return JSON.parse(value);
      } catch (err) {
        return false;
      }
    default:
      return value;
  }
}

function mustBeUnique(value) {
  return !this.otherFieldNames.includes(value);
}

export default {
  name: 'FieldDetail',
  mixins: [validationMixin],
  props: {
    field: {
      type: Object,
      required: true,
    },
    allFields: {
      type: Array,
      required: true,
    },
  },
  data() {
    const f = this.field;

    return {
      name: f ? f.name : '',
      type: f ? f.type : 'string',
      length: f ? f.length || undefined : undefined,
      required: f ? f.required : false,
      unique: f ? f.required : false,
      defaultValue: f ? f.default : undefined,
      index: f ? f.index : false,
      target: f ? f.target || undefined : undefined,
    };
  },
  validations: {
    name: {
      required,
      mustBeCamelCase,
      mustBeUnique,
    },
    length: {
      integer,
      minValue: minValue(1),
    },
    defaultValue: {
      mustBeCorrectType,
    },
  },
  computed: {
    ...mapGetters([
      'dbSupportsStringLength',
      'relationTargets',
      'fieldTypeChoices',
    ]),
    fieldTargetChoices() {
      if (!this.isRelationalField) return [];
      return this.relationTargets.map(m => ({ value: m.name, text: m.name }));
    },
    otherFieldNames() {
      return this.allFields.filter((f) => f !== this.field).map((f) => f.name);
    },
    isTextField() {
      return ['string', 'email', 'url'].includes(this.type);
    },
    isRelationalField() {
      return ['fk', 'm2m'].includes(this.type);
    }
  },
  watch: {
    required() {
      if (this.required) this.defaultValue = undefined;
    },
    unique() {
      if (this.unique) this.defaultValue = undefined;
    },
    '$v.$invalid': function watchError() {
      this.$emit('error', this.$v.$invalid);
    },
  },
  async mounted() {
    await this.$nextTick();
    this.$refs.name.focus();
  },
  updated() {
    this.emitChanges();
  },
  beforeDestroy() {
    this.emitChanges();
  },
  methods: {
    errState(validation) {
      return validation.$error ? false : null;
    },
    emitChanges() {
      if (this.$v.$invalid) return;

      const {
        field,
        name,
        type,
        length,
        unique,
        defaultValue,
        index,
        target,
      } = this;

      if (
        name === field.name
        && type === field.type
        && unique === field.unique
        && this.required === field.required
        && defaultValue === field.default
        && length === field.length
        && index === field.index
        && target === field.target
      ) return;

      this.$emit('update', {
        field,
        data: {
          name,
          type,
          length: (this.isTextField && (length !== '' && length !== null)) ? parseInt(length) : undefined,
          unique,
          default: (defaultValue !== '' && defaultValue !== null) ? defaultValue : undefined,
          required: this.required,
          index,
          target: this.isRelationalField ? target : undefined,
        },
      });
    },
    async removeField() {
      const confirm = await this.$bvModal.msgBoxConfirm(
        `Are you sure you want to remove field "${this.name}"?`,
        {
          okTitle: 'Yes, remove this field',
          okVariant: 'danger',
        },
      );
      if (!confirm) return;
      this.$emit('remove', this.field);
    },
  },
};
</script>
